import React, { useState, useEffect, useRef } from 'react';
import '../css/Dashboard.css';
import { authAPI, phase3API } from '../services/api';
import { AnalysisHistory, BackendAnalysisHistory } from '../types';
import { downloadAuthenticatedFile, fetchWithAuth } from '../config/api';
import ConfirmModal from './ConfirmModal';
import EditableLabel from './EditableLabel';
import { useLabels } from '../config/labelsContext';
import { MOCK_MODE } from '../config/mock';

interface DashboardProps {
  onStartAnalysis: () => void;
  onViewResult: (id: string) => void;
  onOpenSession: (id: string, hintStep?: number) => void | Promise<void>;
  onDeleteSession: (id: string) => Promise<void>;
  showAlert: (message: string, title?: string) => void;
  userId: string;
  refreshKey?: number;
}

type WorkflowItem = Awaited<ReturnType<typeof phase3API.getUserWorkflows>>['workflows'][number];

const normalizeStatus = (raw: unknown) => (raw ?? '').toString().trim().toLowerCase();

// Parse backend "created_at" like "YYYYMMDD" or a session id like "SESSION-YYYYMMDD-...".
// Returns a local Date at midnight, or null if parsing fails.
const parseBackendDate = (createdAt: string, sessionId: string): Date | null => {
  const raw = (createdAt || '').toString().trim();
  const sid = (sessionId || '').toString().trim();

  const fromSessionId = (() => {
    const m = /SESSION-(\d{8})/i.exec(sid);
    return m ? m[1] : '';
  })();

  const candidates = [raw, fromSessionId, sid];
  for (const v of candidates) {
    const s = (v || '').toString().trim();
    if (!s) continue;

    // 1) Numeric date (YYYYMMDD or YYYYMMDDHHMMSS)
    const digits = s.replace(/\D/g, '');
    if (digits.length >= 8) {
      const y = Number(digits.slice(0, 4));
      const mo = Number(digits.slice(4, 6));
      const d = Number(digits.slice(6, 8));
      const h = digits.length >= 10 ? Number(digits.slice(8, 10)) : 0;
      const mi = digits.length >= 12 ? Number(digits.slice(10, 12)) : 0;
      const sec = digits.length >= 14 ? Number(digits.slice(12, 14)) : 0;
      if (y >= 2000 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
        // Backend stores UTC; construct a UTC Date so the formatter converts to JST.
        return new Date(Date.UTC(y, mo - 1, d, h, mi, sec));
      }
    }

    // 2) Fallback: try Date.parse (ISO strings, locale strings, etc.)
    const t = Date.parse(s);
    if (!Number.isNaN(t)) return new Date(t);
  }

  return null;
};

const isSameMonth = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

const getDisplayTitle = (title: unknown, fallback: string) => {
  const v = (title || '').toString().trim();
  return v || fallback;
};

const tokyoDateFormatter = new Intl.DateTimeFormat('ja-JP', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const formatCreatedAtLabel = (createdAt: unknown, sessionId: string, unknownFallback = '不明') => {
  const raw = (createdAt || '').toString().trim();

  const parsed = parseBackendDate(raw, sessionId);
  if (!parsed) return raw || unknownFallback;
  return tokyoDateFormatter.format(parsed);
};

const Dashboard: React.FC<DashboardProps> = ({
  onStartAnalysis,
  onViewResult,
  onOpenSession,
  onDeleteSession,
  showAlert,
  userId,
  refreshKey,
}) => {
  const { labels } = useLabels();
  const [historyList, setHistoryList] = useState<AnalysisHistory[]>([]);
  const [historyWaiting, setHistoryWaiting] = useState(false);
  const historyPollAttemptsRef = useRef(0);
  const historyLenRef = useRef(0);
  const historyErrRef = useRef(0);
  const prevUserIdRef = useRef('');
  const [usageCount, setUsageCount] = useState(0);
  const [activeWorkflows, setActiveWorkflows] = useState<WorkflowItem[]>([]);
  const [workflowsWaiting, setWorkflowsWaiting] = useState(false);
  const historyInFlightRef = useRef(false);
  const historyReqIdRef = useRef(0);
  const workflowsInitDoneRef = useRef(false);
  const workflowsInFlightRef = useRef(false);
  const workflowsReqIdRef = useRef(0);
  const deletedSessionTombstonesRef = useRef<Record<string, number>>({});
  const prevActiveSessionIdsRef = useRef<Set<string>>(new Set());
  const [downloadingSessionId, setDownloadingSessionId] = useState<string>('');
  const [openingSessionId, setOpeningSessionId] = useState<string>('');
  const [deletingSessionIds, setDeletingSessionIds] = useState<Record<string, boolean>>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmSessionId, setDeleteConfirmSessionId] = useState('');
  const [prepOverviewReady, setPrepOverviewReady] = useState(false);
  const [prepPdfReady, setPrepPdfReady] = useState(false);
  const [prepWarning, setPrepWarning] = useState('');

  const formatStatus = (raw: string | undefined) => {
    const s = (raw || '').toString();
    return labels.dashboard.statusMap[s] || s || labels.dashboard.statusMap['in_progress'];
  };

  const formatJobStatus = (raw: string | undefined) => {
    const s = (raw || '').toString();
    return labels.dashboard.jobStatusMap[s] || s;
  };

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let intervalId: number | null = null;
    const abortRef = { current: null as AbortController | null };
    const REQUEST_TIMEOUT_MS = 15_000;

    if (prevUserIdRef.current !== userId) {
      prevUserIdRef.current = userId;
      historyPollAttemptsRef.current = 0;
      historyLenRef.current = 0;
      historyErrRef.current = 0;
      setHistoryWaiting(true);
    } else if (historyLenRef.current === 0) {
      setHistoryWaiting(true);
    }

    const mapHistory = (history: BackendAnalysisHistory[]): AnalysisHistory[] => {
      // Map backend history to UI model - only show sessions fully completed.
      return history
        .filter((item: BackendAnalysisHistory) => {
          const rawStatus = normalizeStatus(item.status);
          return rawStatus === 'completed';
        })
        .map((item: BackendAnalysisHistory): AnalysisHistory => {
          const rawStatus = normalizeStatus(item.status);
          const step = Number(item.current_step || 0);
          const status: AnalysisHistory['status'] = 'completed';
          return {
            id: item.session_id,
            title: item.title,
            patentCount: item.patent_count,
            createdAt: item.created_at || new Date().toLocaleString('ja-JP'),
            viewKey: item.session_id.substring(0, 20) + '...',
            status,
            currentStep: step || undefined,
            rawStatus: rawStatus || undefined,
          };
        });
    };

    const fetchHistory = async () => {
      if (!userId) return;
      if (historyInFlightRef.current) return;
      historyInFlightRef.current = true;
      const reqId = (historyReqIdRef.current += 1);
      try {
        abortRef.current = new AbortController();
        const t = window.setTimeout(() => abortRef.current?.abort(), REQUEST_TIMEOUT_MS);
        try {
          const data = await authAPI.getUserHistory(userId, { signal: abortRef.current.signal });
          if (cancelled) return;
          if (reqId !== historyReqIdRef.current) return;
          const mappedHistory: AnalysisHistory[] = data?.history ? mapHistory(data.history) : [];
          historyLenRef.current = mappedHistory.length;
          setHistoryList(mappedHistory);
          // Monthly usage: sum of patents analyzed in completed sessions of the current month.
          const now = new Date();
          const monthlyUsage = mappedHistory.reduce((sum, h) => {
            const d = parseBackendDate(h.createdAt, h.id);
            if (!d) return sum;
            if (!isSameMonth(d, now)) return sum;
            const n = Number(h.patentCount || 0);
            return sum + (Number.isFinite(n) ? n : 0);
          }, 0);
          setUsageCount(Math.max(0, Math.floor(monthlyUsage)));
          if (mappedHistory.length > 0) {
            setHistoryWaiting(false);
            historyPollAttemptsRef.current = 0;
            historyErrRef.current = 0;
          } else {
            // If the backend responded successfully but there is simply no history yet,
            // don't keep showing a long "loading" state. We'll still poll in the background.
            setHistoryWaiting(false);
          }
        } finally {
          window.clearTimeout(t);
        }
        if (cancelled) return;
      } catch (error) {
        if (cancelled) return;
        const err = error as { name?: string };
        if (err?.name === 'AbortError') return;
        historyErrRef.current += 1;
        // eslint-disable-next-line no-console
        console.error('履歴の取得に失敗しました:', error);
      } finally {
        historyInFlightRef.current = false;
      }
    };

    fetchHistory();

    const tick = () => {
      if (cancelled) return;

      // If data exists and there are no active workflows, stop polling.
      if (historyLenRef.current > 0 && activeWorkflows.length === 0) {
        if (intervalId) window.clearInterval(intervalId);
        setHistoryWaiting(false);
        return;
      }

      // If no active workflows and still empty, poll a few times then stop.
      if (activeWorkflows.length === 0 && historyLenRef.current === 0) {
        const MAX_ATTEMPTS = 3; // ~30s
        if (historyPollAttemptsRef.current >= MAX_ATTEMPTS) {
          if (intervalId) window.clearInterval(intervalId);
          setHistoryWaiting(false);
          return;
        }
        historyPollAttemptsRef.current += 1;
      }

      // If we keep failing to fetch, stop waiting sooner to avoid "infinite loading" feel.
      if (activeWorkflows.length === 0 && historyErrRef.current >= 3) {
        if (intervalId) window.clearInterval(intervalId);
        setHistoryWaiting(false);
        return;
      }

      fetchHistory();
    };

    intervalId = window.setInterval(tick, 10000);
    return () => {
      cancelled = true;
      abortRef.current?.abort();
      historyInFlightRef.current = false;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [userId, activeWorkflows.length, refreshKey]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let intervalId: number | null = null;
    const abortRef = { current: null as AbortController | null };
    const REQUEST_TIMEOUT_MS = 15_000;

    const fetchWorkflows = async () => {
      if (MOCK_MODE) {
        const mockWorkflows = [
          {
            session_id: 'SESSION-20260325-mock001',
            title: 'EV・移動体の自動位置合わせワイヤレス充電',
            status: 'in_progress',
            current_step: 16,
            created_at: '20260325',
            async_task_id: null,
            progress_current: 4,
            progress_total: 7,
            progress_message: '特許マップに載せる技術のまとまりを整理しています',
          },
          {
            session_id: 'SESSION-20260324-mock002',
            title: '無人飛行機の着陸位置補正アルゴリズム',
            status: 'in_progress',
            current_step: 13,
            created_at: '20260324',
            async_task_id: 'job-mock-002',
            progress_current: 7,
            progress_total: 7,
            progress_message: '検索条件の作成が完了しました',
          },
          {
            session_id: 'SESSION-20260323-mock003',
            title: '自動運転車両のセンサーフュージョン技術',
            status: 'excel_ready',
            current_step: 23,
            created_at: '20260323',
            async_task_id: null,
            progress_current: 7,
            progress_total: 7,
            progress_message: 'Excel生成が完了しました',
          },
        ] as WorkflowItem[];
        setActiveWorkflows(mockWorkflows);
        if (!workflowsInitDoneRef.current) {
          workflowsInitDoneRef.current = true;
          setWorkflowsWaiting(false);
        }
        return;
      }

      // Avoid piling up aborted requests when the network/server is slow.
      if (workflowsInFlightRef.current) return;
      workflowsInFlightRef.current = true;
      const reqId = (workflowsReqIdRef.current += 1);

      try {
        abortRef.current = new AbortController();
        const t = window.setTimeout(() => abortRef.current?.abort(), REQUEST_TIMEOUT_MS);
        let data: Awaited<ReturnType<typeof phase3API.getUserWorkflows>> | null = null;
        try {
          data = await phase3API.getUserWorkflows(userId, { signal: abortRef.current.signal });
        } finally {
          window.clearTimeout(t);
        }
        if (cancelled) return;
        if (reqId !== workflowsReqIdRef.current) return;
        if (!data) return;
        // Hide sessions that the user just deleted optimistically.
        // This prevents "it came back, so I clicked delete again" behavior while the backend
        // deletion is still in-flight or eventually consistent.
        const TOMBSTONE_TTL_MS = 60_000;
        const now = Date.now();
        for (const [sid, ts] of Object.entries(deletedSessionTombstonesRef.current)) {
          if (!ts || now - ts > TOMBSTONE_TTL_MS) {
            delete deletedSessionTombstonesRef.current[sid];
          }
        }
        const isTombstoned = (sid: string) => {
          const ts = deletedSessionTombstonesRef.current[sid];
          return typeof ts === 'number' && now - ts <= TOMBSTONE_TTL_MS;
        };

        const allWorkflows = data.workflows || [];
        // Keep excel_ready sessions in active section (user must download Excel first).
        // Only truly "completed" (downloaded) sessions are excluded.
        const actives = allWorkflows
          .filter((w) => normalizeStatus(w.status) !== 'completed')
          .filter((w) => !isTombstoned(String(w.session_id || '')))
          // Sort by created_at desc to keep stable order regardless of status changes
          .sort((a, b) => {
            const ca = String(a.created_at || '');
            const cb = String(b.created_at || '');
            return cb.localeCompare(ca);
          });

        prevActiveSessionIdsRef.current = new Set(actives.map((w) => String(w.session_id || '')));

        // Always use fresh backend state. A prior revision of this block
        // preserved a cached higher step when the backend returned a lower
        // one (to avoid visual jitter on async handlers). That masked
        // legitimate rollbacks — e.g., after extract_realization_methods ran
        // prematurely with empty final_patents and generate_formulas later
        // overwrote step=16 → step=13, the dashboard kept showing 16 and
        // routed users back to the broken realization screen. Trust the
        // backend.
        setActiveWorkflows(() => actives.slice(0, 3));
      } catch (e) {
        if (cancelled) return;
        const err = e as { name?: string };
        if (err?.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.warn('ワークフロー一覧の取得に失敗しました', e);
      } finally {
        workflowsInFlightRef.current = false;
        // Mark initial fetch done so we don't keep showing "loading" when empty is valid.
        if (!workflowsInitDoneRef.current) {
          workflowsInitDoneRef.current = true;
          setWorkflowsWaiting(false);
        }
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchWorkflows();
    };

    if (!workflowsInitDoneRef.current) {
      setWorkflowsWaiting(true);
    }
    fetchWorkflows(); // initial
    intervalId = window.setInterval(fetchWorkflows, 5000);
    window.addEventListener('focus', fetchWorkflows);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      abortRef.current?.abort();
      workflowsInFlightRef.current = false;
      if (intervalId) window.clearInterval(intervalId);
      window.removeEventListener('focus', fetchWorkflows);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [userId, onOpenSession, refreshKey]);

  const handleDownload = async (id: string) => {
    if (!id) return;
    if (downloadingSessionId === id) return;

    setDownloadingSessionId(id);

    try {
      const response = await fetchWithAuth(`/api/phase3/export?session_id=${id}&format=excel`);
      if (!response.ok) throw new Error(labels.dashboard.downloadError);

      const data = await response.json();
      const fileUrl = (data.file_url as string | undefined) || '';
      if (!fileUrl) {
        throw new Error(labels.dashboard.downloadError);
      }
      // file_url may be either an S3 presigned URL (auth-free) or a relative
      // /api/phase3/download/... path that requires the Cognito Authorization
      // header. downloadAuthenticatedFile handles both.
      await downloadAuthenticatedFile(fileUrl, `patent_analysis_${id}.xlsx`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Download error:', error);
      showAlert(labels.dashboard.downloadError);
    } finally {
      setDownloadingSessionId('');
    }
  };

  const executeDeleteActiveSession = async (sid: string) => {
    const id = (sid || '').toString();
    if (!id) return;
    if (deletingSessionIds[id]) return;

    // Optimistically hide it immediately.
    deletedSessionTombstonesRef.current[id] = Date.now();
    const removed = activeWorkflows.find((w) => w.session_id === id) || null;
    setActiveWorkflows((prev) => prev.filter((w) => w.session_id !== id));
    setDeletingSessionIds((prev) => ({ ...prev, [id]: true }));

    try {
      await onDeleteSession(id);
    } catch (e) {
      // If the delete failed, restore the session in the UI.
      delete deletedSessionTombstonesRef.current[id];
      if (removed) {
        setActiveWorkflows((prev) => [removed, ...prev].slice(0, 3));
      }
      // eslint-disable-next-line no-console
      console.warn('Failed to delete session:', e);
      showAlert(labels.dashboard.deleteSessionError, labels.common.error);
    } finally {
      setDeletingSessionIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleDeleteActiveSession = (sid: string) => {
    const id = (sid || '').toString();
    if (!id) return;
    if (deletingSessionIds[id]) return;
    setDeleteConfirmSessionId(id);
    setDeleteConfirmOpen(true);
  };

  const handleStart = () => {
    setPrepWarning('');
    if (!prepOverviewReady || !prepPdfReady) {
      showAlert(labels.dashboard.checkWarning);
      return;
    }
    if (activeWorkflows.length >= 3) {
      showAlert(labels.dashboard.maxSessionsWarning, labels.modals.noticeTitle);
      return;
    }
    onStartAnalysis();
  };

  // Exclude sessions already shown in the active section to avoid duplicates.
  const activeSessionIds = new Set(activeWorkflows.map((w) => w.session_id));
  const displayedHistory = historyList.filter((h) => !activeSessionIds.has(h.id)).slice(0, 6);

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <ConfirmModal
          open={deleteConfirmOpen}
          title={labels.dashboard.deleteSessionTitle}
          message={labels.dashboard.deleteSessionConfirm}
          confirmLabel={labels.modals.confirmDeleteButton}
          cancelLabel={labels.modals.confirmCancelButton}
          destructive
          onCancel={() => {
            setDeleteConfirmOpen(false);
            setDeleteConfirmSessionId('');
          }}
          onConfirm={() => {
            const id = deleteConfirmSessionId;
            setDeleteConfirmOpen(false);
            setDeleteConfirmSessionId('');
            void executeDeleteActiveSession(id);
          }}
        />
        <div className="dashboard-header">
          <div className="dashboard-header-row">
            <div className="dashboard-header-left">
              <h1 className="dashboard-title">
                <span className="dashboard-title-icon" aria-hidden="true">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 10.5L12 3l9 7.5"></path>
                    <path d="M5 9.5V21h14V9.5"></path>
                    <path d="M9 21v-7h6v7"></path>
                  </svg>
                </span>
                <span>
                  <EditableLabel
                    labelKey="dashboard.pageTitle"
                    defaultValue="特許マップ作成システム：管理画面"
                  />
                </span>
              </h1>
              <p className="dashboard-subtitle">
                <EditableLabel
                  labelKey="dashboard.subtitle"
                  defaultValue="特許マップの作成 と 管理 を行います。"
                />
              </p>
            </div>
            <div className="usage-section usage-section-compact" aria-label="月間利用状況">
              <h3>
                <EditableLabel labelKey="dashboard.monthlyUsageTitle" defaultValue="月間利用状況" />
              </h3>
              <div className="usage-count">
                <span className="usage-dot">●</span>
                <span className="current-usage">{usageCount}</span>
                <span className="usage-unit">
                  <EditableLabel labelKey="dashboard.usageUnit" defaultValue="件" />
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="create-map-section">
          <div className="create-map-header">
            <h2>
              <EditableLabel labelKey="dashboard.createMapTitle" defaultValue="特許マップ作成" />
            </h2>

            <p className="create-map-description">
              <EditableLabel
                labelKey="dashboard.createMapDescription"
                defaultValue="指定した分野（入力した分野）について、特許情報を収集して特許マップを作成します。"
              />
            </p>
            <div className="workflow-step-description phase-description-banner">
              <EditableLabel
                labelKey="dashboard.createMapBanner"
                defaultValue="チェック欄を入力後、下の「特許マップの作成開始」を押してください。"
              />
            </div>
          </div>
          <div className="create-map-body">
            <div className="create-map-notes">
              <div className="create-map-notes-title">
                <EditableLabel labelKey="dashboard.prepTitle" defaultValue="準備するもの" />
              </div>
              <ul className="create-map-notes-list">
                <li>
                  <label className={`prep-check-inline ${prepOverviewReady ? 'checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={prepOverviewReady}
                      onChange={(e) => {
                        setPrepOverviewReady(e.target.checked);
                        setPrepWarning('');
                      }}
                    />
                    <EditableLabel
                      labelKey="dashboard.prepOverviewLabel"
                      defaultValue="特許マップを作成する分野に関する情報（利用分野、技術の特徴、課題、効果）"
                    />
                  </label>
                </li>
                <li>
                  <label className={`prep-check-inline ${prepPdfReady ? 'checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={prepPdfReady}
                      onChange={(e) => {
                        setPrepPdfReady(e.target.checked);
                        setPrepWarning('');
                      }}
                    />
                    <EditableLabel
                      labelKey="dashboard.prepPdfLabel"
                      defaultValue="特許マップの対象分野の特許例（PDF形式）５～10件程度"
                    />
                  </label>
                </li>
              </ul>
            </div>

            {prepWarning && <div className="prep-warning">{prepWarning}</div>}

            <button className="start-analysis-btn" onClick={handleStart}>
              <EditableLabel labelKey="dashboard.startButton" defaultValue="特許マップの作成開始" />
            </button>
          </div>
        </div>

        <div className="history-section">
          <div className="flex-between">
            <h3>
              <EditableLabel
                labelKey="dashboard.activeSessionsTitle"
                defaultValue="進行中セッション（最大3）"
              />
            </h3>
            {workflowsWaiting && (
              <span className="dashboard-section-loading" aria-live="polite">
                <EditableLabel labelKey="dashboard.loadingText" defaultValue="情報を取得中です" />
              </span>
            )}
          </div>
          {activeWorkflows.length === 0 ? (
            <div className="empty-state">
              <p>
                {/* An empty list is a valid result, not a pending fetch. Showing the
                    loading text here left users staring at "情報を取得中です" forever
                    whenever they had no session in progress. */}
                {workflowsWaiting ? (
                  <EditableLabel labelKey="dashboard.loadingText" defaultValue="情報を取得中です" />
                ) : (
                  <EditableLabel
                    labelKey="dashboard.noActiveSessions"
                    defaultValue="進行中のセッションはありません"
                  />
                )}
              </p>
            </div>
          ) : (
            <div className="history-list">
              {activeWorkflows.map((w) => {
                // Overall progress: don't show 100% until Phase 3 is reached.
                // We map backend step range [0..23] to 0..100%.
                const MAX_STEP = 23;
                const step = Math.max(0, Number(w.current_step || 0));
                const stepClamped = Math.min(MAX_STEP, step);
                const pct = Math.min(100, Math.floor((stepClamped / MAX_STEP) * 100));
                const statusLabel = formatStatus(w.status);
                const jobStatusLabel = w.job?.status ? formatJobStatus(w.job.status) : '';
                const isRetrying = Boolean(w.job?.is_retrying);
                const queue = w.job?.queue;
                const showQueue =
                  w.job?.status === 'queued' && queue && typeof queue.queue_position === 'number';
                const isBackgroundStatus = new Set([
                  'analyzing_pdfs',
                  'generating_formulas',
                  'executing_final_search',
                  'extracting_realization_methods',
                  'extracting_components',
                  'judging_patent_quality',
                  'generating_excel',
                ]).has(w.status);
                const hasBackgroundWork =
                  w.job?.status === 'running' ||
                  w.job?.status === 'queued' ||
                  Boolean(w.async_task_id) ||
                  isBackgroundStatus;
                // Blink ONLY when the user must take an explicit action.
                // Step mapping (backend):
                // - 4: PDF upload required
                // - 13: Select formulas required
                // - 16: Select realization methods required
                // - completed: View results
                const isExcelReady = normalizeStatus(w.status) === 'excel_ready';
                const isCompleted = normalizeStatus(w.status) === 'completed';
                const actionPrompt = isExcelReady
                  ? labels.dashboard.actionDownloadExcel
                  : w.status === 'in_progress' && !hasBackgroundWork
                    ? step === 4
                      ? labels.dashboard.actionUploadPdf
                      : step === 13
                        ? labels.dashboard.actionSelectFormula
                        : step === 16
                          ? labels.dashboard.actionSelectRealization
                          : ''
                    : '';
                const shouldBlinkResume = Boolean(actionPrompt);
                const resumeLabel = actionPrompt
                  ? actionPrompt
                  : isCompleted
                    ? labels.dashboard.goToExecution
                    : labels.dashboard.resumeLabel;
                const workflowTitle = getDisplayTitle(w.title, `セッション ${w.session_id}`);

                return (
                  <div key={w.session_id} className="history-card">
                    <div className="history-card-left">
                      <div
                        className={`status-icon ${isExcelReady || isCompleted ? 'completed' : 'processing'}`}
                      >
                        {isExcelReady || isCompleted ? '✓' : '⟳'}
                      </div>
                      <div className="history-info">
                        <h4 className="history-title">{workflowTitle}</h4>
                        <div className="history-meta">
                          <span>
                            <EditableLabel
                              labelKey="dashboard.sessionIdLabel"
                              defaultValue="セッションID:"
                            />{' '}
                            {w.session_id}
                          </span>
                        </div>
                        <div className="history-meta">
                          <span>
                            <EditableLabel
                              labelKey="dashboard.progressLabel"
                              defaultValue="進捗:"
                            />{' '}
                            {statusLabel}
                            {isRetrying ? (
                              <>
                                （
                                <span className="retry-status-label">
                                  <EditableLabel
                                    labelKey="dashboard.retryingLabel"
                                    defaultValue="リトライ中"
                                  />
                                </span>
                                ）
                              </>
                            ) : jobStatusLabel ? (
                              `（ジョブ: ${jobStatusLabel}）`
                            ) : (
                              ''
                            )}
                          </span>
                        </div>
                        <div className="history-meta">
                          <span>
                            <EditableLabel
                              labelKey="dashboard.createdAtLabel"
                              defaultValue="作成開始日時:"
                            />{' '}
                            {formatCreatedAtLabel(
                              w.created_at,
                              w.session_id,
                              labels.dashboard.dateUnknown
                            )}
                          </span>
                        </div>
                        {showQueue && (
                          <div className="history-meta">
                            <span>
                              {labels.dashboard.queueWaiting} {queue.queue_position}
                              {typeof queue.queued_total === 'number'
                                ? ` / ${labels.dashboard.queueWaitingPosition}${queue.queued_total}`
                                : ''}
                              {typeof queue.concurrency === 'number'
                                ? ` / ${labels.dashboard.queueConcurrency}${queue.concurrency}`
                                : ''}
                              ）
                            </span>
                          </div>
                        )}
                        {/* Hide verbose backend progress message; show only bar/percent. */}
                        <div className="active-progress">
                          <div className="active-progress-bar">
                            <div className="active-progress-fill" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="active-progress-pct">{pct}%</div>
                        </div>
                      </div>
                    </div>
                    <div className="history-card-right">
                      <button
                        className={`view-btn${shouldBlinkResume ? ' resume-attention' : ''}${openingSessionId === w.session_id ? ' is-loading' : ''}`}
                        disabled={Boolean(openingSessionId)}
                        onClick={async () => {
                          if (openingSessionId) return;
                          setOpeningSessionId(w.session_id);
                          try {
                            await onOpenSession(w.session_id, Number(w.current_step || 0));
                          } finally {
                            setOpeningSessionId('');
                          }
                        }}
                      >
                        {openingSessionId === w.session_id ? (
                          <span className="btn-spinner" aria-hidden />
                        ) : (
                          resumeLabel
                        )}
                      </button>
                      <button
                        className="download-btn"
                        onClick={() => handleDeleteActiveSession(w.session_id)}
                        title="セッションを削除"
                        disabled={Boolean(deletingSessionIds[w.session_id])}
                      >
                        {deletingSessionIds[w.session_id] ? (
                          <EditableLabel
                            labelKey="dashboard.deletingButton"
                            defaultValue="削除中..."
                          />
                        ) : (
                          <EditableLabel labelKey="dashboard.deleteButton" defaultValue="🗑 削除" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="history-section">
          <div className="flex-between">
            <h3>
              <EditableLabel
                labelKey="dashboard.completedHistoryTitle"
                defaultValue="完了した特許解析"
              />
            </h3>
            {historyWaiting && (
              <span className="dashboard-section-loading" aria-live="polite">
                {labels.dashboard.loadingText}
              </span>
            )}
          </div>

          {historyList.length === 0 ? (
            <div className="empty-state">
              {historyWaiting ? (
                <>
                  <p>
                    <EditableLabel
                      labelKey="dashboard.loadingText"
                      defaultValue="情報を取得中です"
                    />
                  </p>
                  <p className="empty-state-hint">{labels.dashboard.historyWaitingHint}</p>
                </>
              ) : (
                <>
                  {activeWorkflows.length > 0 ? (
                    <>
                      <p>
                        <EditableLabel
                          labelKey="dashboard.emptyHistoryWithActive"
                          defaultValue="完了した特許解析はまだありません"
                        />
                      </p>
                      <p className="empty-state-hint">
                        <EditableLabel
                          labelKey="dashboard.emptyHistoryHintWithActive"
                          defaultValue="進行中セッションが完了すると、ここに履歴が表示されます"
                        />
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        <EditableLabel
                          labelKey="dashboard.emptyHistoryNoActive"
                          defaultValue="まだ特許解析の履歴がありません"
                        />
                      </p>
                      <p className="empty-state-hint">
                        <EditableLabel
                          labelKey="dashboard.emptyHistoryHintNoActive"
                          defaultValue="上の「特許マップの作成開始」ボタンから開始できます"
                        />
                      </p>
                    </>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="history-list">
              {displayedHistory.map((item) => (
                <div key={item.id} className="history-card">
                  <div className="history-card-left">
                    {item.status === 'completed' && <div className="status-icon completed">✓</div>}
                    {item.status === 'processing' && (
                      <div className="status-icon processing">⟳</div>
                    )}
                    {item.status === 'failed' && <div className="status-icon failed">✕</div>}

                    <div className="history-info">
                      <h4 className="history-title">
                        {getDisplayTitle(
                          item.title,
                          `特許解析結果 #${item.id.replace('demo-', '')}`
                        )}
                        <span className="edit-icon">✎</span>
                      </h4>
                      <div className="history-meta">
                        <span>セッションID: {item.id}</span>
                      </div>
                      <div className="history-meta">
                        <span>特許数: {item.patentCount}</span>
                      </div>
                      <div className="history-meta">
                        <span>作成開始日時: {formatCreatedAtLabel(item.createdAt, item.id)}</span>
                      </div>
                      <div className="history-meta view-key">
                        <span>表示キー: {item.viewKey}</span>
                      </div>
                    </div>
                  </div>

                  <div className="history-card-right">
                    {item.status === 'completed' && (
                      <span className="status-badge completed">
                        <EditableLabel labelKey="dashboard.statusCompleted" defaultValue="完成" />
                      </span>
                    )}
                    <button
                      className="view-btn"
                      onClick={() => onViewResult(item.id)}
                      disabled={item.status !== 'completed'}
                    >
                      <EditableLabel labelKey="dashboard.viewButton" defaultValue="👁 表示" />
                    </button>
                    <button
                      className={`download-btn${downloadingSessionId === item.id ? ' is-loading' : ''}`}
                      onClick={() => handleDownload(item.id)}
                      disabled={item.status !== 'completed' || downloadingSessionId === item.id}
                    >
                      {downloadingSessionId === item.id ? (
                        <>
                          <span className="spinner" />
                          <EditableLabel
                            labelKey="dashboard.downloadPreparing"
                            defaultValue="Excelファイルを準備しています"
                          />
                        </>
                      ) : (
                        <>
                          <EditableLabel
                            labelKey="dashboard.downloadButton"
                            defaultValue="⬇ ダウンロード"
                          />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
              {historyList.length > displayedHistory.length && (
                <div className="history-limit-note">
                  {labels.dashboard.historyLimitPrefix}
                  {displayedHistory.length}
                  {labels.dashboard.historyLimitNote}（全{historyList.length}件）
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
